import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Block readiness (MYD-515): a way for a block to say "I am not drawn yet".
 *
 * The presenter's story readiness covers data and image URLs. A map has a
 * further wait, for its tiles, that only the map itself knows the end of
 * (MapLibre's `idle`). Without this, Start could enable while the first
 * slide's map was still blank, and the audience would watch it fill in.
 *
 * Opt-in: a block calls `useReportReady(ready)`. Inside a
 * `BlockReadinessProvider` it counts as pending from mount until `ready` is
 * true; anywhere else (the editor, the presenter after Start, thumbnails) the
 * hook does nothing at all.
 *
 * Registration happens in effects. A parent's effects run after its
 * children's, so a component that wraps the blocks and sets a flag in its own
 * effect knows every block below it has registered: see `ReadinessProbe` in
 * the presenter.
 */

interface Registrar {
	add: () => number
	resolve: (key: number) => void
	remove: (key: number) => void
}

const BlockReadinessContext = createContext<Registrar | null>(null)

export const BlockReadinessProvider = BlockReadinessContext.Provider

/** For the component that waits: a registrar to provide, and how many blocks are still pending. */
export function useReadinessTracker(): { registrar: Registrar; pending: number } {
	const pendingKeys = useRef(new Set<number>())
	const nextKey = useRef(0)
	const [pending, setPending] = useState(0)

	const sync = useCallback(() => setPending(pendingKeys.current.size), [])
	const registrar = useMemo<Registrar>(
		() => ({
			add: () => {
				const key = nextKey.current++
				pendingKeys.current.add(key)
				sync()
				return key
			},
			resolve: key => {
				if (pendingKeys.current.delete(key)) sync()
			},
			remove: key => {
				if (pendingKeys.current.delete(key)) sync()
			},
		}),
		[sync],
	)
	return { registrar, pending }
}

/** For a block: pending until `ready`, if anyone is waiting. */
export function useReportReady(ready: boolean) {
	const registrar = useContext(BlockReadinessContext)
	const key = useRef<number | null>(null)

	useEffect(() => {
		if (!registrar) return
		key.current = registrar.add()
		return () => {
			if (key.current !== null) registrar.remove(key.current)
			key.current = null
		}
	}, [registrar])

	useEffect(() => {
		if (ready && registrar && key.current !== null) registrar.resolve(key.current)
	}, [ready, registrar])
}
